// Admin → Textos: TODOS los textos del sitio editables en ES/EN/RU/FR.
// Campo vacío = texto por defecto (se muestra como placeholder).
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, Check, Search } from 'lucide-react';

import { getContenidoPortada, putContenidoPortada } from '../../lib/adminApi';
import { aplicarTextos } from '../../i18n/overrides';
import { ET } from '../../lib/empresasTxt';
import es from '../../i18n/locales/es.json';
import en from '../../i18n/locales/en.json';
import ru from '../../i18n/locales/ru.json';
import fr from '../../i18n/locales/fr.json';

const LANGS = ['es', 'en', 'ru', 'fr'];
const RX_CLAVE = /^[A-Za-z0-9_.-]{1,150}$/;
const MAX_VISIBLES = 80;
// Solo los textos que se ven en la portada (inicio).
const SECCIONES_PORTADA = ['hero', 'nav', 'clases', 'bonos', 'profesores', 'eventos', 'galeria', 'galeria_diaria', 'proximas', 'redes', 'video_diario', 'videos', 'footer'];
const CLAVES_EMPRESAS_PORTADA = ['empresas_txt.eyebrow', 'empresas_txt.titulo', 'empresas_txt.sub'];
const esDePortada = (c) => SECCIONES_PORTADA.includes(c.split('.')[0]) || CLAVES_EMPRESAS_PORTADA.includes(c);

function aplanar(obj, prefijo = '', out = {}) {
  for (const [k, v] of Object.entries(obj || {})) {
    const c = prefijo ? `${prefijo}.${k}` : k;
    if (v && typeof v === 'object') aplanar(v, c, out);
    else if (typeof v === 'string') out[c] = v;
  }
  return out;
}

// Textos por defecto por idioma (JSON de la web + textos de empresas).
const DEFECTOS = (() => {
  const d = {};
  const fuentes = { es, en, ru, fr };
  for (const l of LANGS) {
    d[l] = aplanar(fuentes[l]);
    for (const [k, v] of Object.entries(ET[l] || {})) d[l][`empresas_txt.${k}`] = v;
  }
  for (const [k, v] of Object.entries(ET.es)) d.es[`empresas_txt.${k}`] = v;
  return d;
})();
const CLAVES_TODAS = Object.keys(DEFECTOS.es).filter((c) => RX_CLAVE.test(c)).sort();

export default function AdminTextos() {
  const { t } = useTranslation();
  const [guardados, setGuardados] = useState({});   // clave → {es,en,ru,fr} ya en el servidor
  const [cambios, setCambios] = useState({});       // clave → {lang: valor} sin guardar
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);
  const [q, setQ] = useState('');
  const [seccion, setSeccion] = useState('');
  
  useEffect(() => {
    getContenidoPortada()
      .then((r) => {
        const g = {};
        for (const f of r.data || []) g[f.clave] = Object.fromEntries(LANGS.map((l) => [l, f[`valor_${l}`] || '']));
        setGuardados(g);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  const claves = useMemo(() => CLAVES_TODAS.filter(esDePortada), []);
  const secciones = useMemo(() => {
    const m = new Map();
    claves.forEach((c) => { const s = c.split('.')[0]; m.set(s, (m.get(s) || 0) + 1); });
    return [...m.entries()];
  }, [claves]);

  const busca = q.trim().toLowerCase();
  const visibles = useMemo(() => {
    if (!busca && !seccion) return [];
    return claves.filter((c) => {
      if (seccion && c.split('.')[0] !== seccion) return false;
      if (!busca) return true;
      return c.toLowerCase().includes(busca) || LANGS.some((l) => (DEFECTOS[l][c] || '').toLowerCase().includes(busca) || ((guardados[c] || {})[l] || '').toLowerCase().includes(busca));
    }).slice(0, MAX_VISIBLES);
  }, [claves, busca, seccion, guardados]);

  const valor = (c, l) => (cambios[c] && cambios[c][l] !== undefined ? cambios[c][l] : (guardados[c] || {})[l] || '');
  const set = (c, l) => (e) => {
    const v = e.target.value; setOk(false);
    setCambios((x) => ({ ...x, [c]: { ...(x[c] || {}), [l]: v } }));
  };
  const pendientes = Object.keys(cambios).length;

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const envio = {};
      for (const c of Object.keys(cambios)) envio[c] = Object.fromEntries(LANGS.map((l) => [l, valor(c, l)]));
      await putContenidoPortada(envio);
      setGuardados((g) => ({ ...g, ...envio }));
      setCambios({});
      // Aplica ya en esta pestaña (los textos vaciados vuelven al defecto al recargar la web).
      aplicarTextos(Object.fromEntries(LANGS.map((l) => [l, Object.fromEntries(Object.entries(envio).map(([c, v]) => [c, v[l]]))])));
      setOk(true);
    } catch (e) { setError(e); } finally { setGuardando(false); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.nav.portada', { defaultValue: 'Textos de portada' })}</h1>
          <p className="admin-page-sub">{t('admin.textos.sub', { defaultValue: 'Aquí puedes cambiar los textos de la portada en los 4 idiomas. Busca por palabra o elige una sección. Si dejas un campo vacío se usa el texto original (se ve en gris). Si un texto lleva palabras entre dobles llaves (por ejemplo n o dias), déjalas igual: se sustituyen solas.' })}</p>
        </div>
      </header>
      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}
      {!loading && (
        <>
          <div className="admin-filtros" style={{ marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <Search size={16} />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('admin.textos.buscar', { defaultValue: 'Buscar un texto (ej. "vídeos", "reservar", "regalo")…' })} style={{ flex: 1 }} />
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {secciones.map(([s, n]) => (
              <button type="button" key={s} className={'admin-filtro-pill' + (seccion === s ? ' activo' : '')} onClick={() => setSeccion(seccion === s ? '' : s)}>{s} ({n})</button>
            ))}
          </div>

          {visibles.length === 0 && <p className="admin-page-sub">{t('admin.textos.vacio', { defaultValue: 'Elige una sección o escribe en el buscador para ver los textos.' })}</p>}
          <div className="mw-textos">
            {visibles.map((c) => (
              <section key={c} className="mw-texto-bloque">
                <h3 style={{ fontSize: 13, fontFamily: 'monospace', wordBreak: 'break-all' }}>{c}</h3>
                <div className="admin-form-grid">
                  {LANGS.map((l) => {
                    const def = DEFECTOS[l][c] || DEFECTOS.es[c] || '';
                    const props = { value: valor(c, l), onChange: set(c, l), placeholder: def };
                    return (
                      <div key={l} className="modal-campo">
                        <label>{l.toUpperCase()}</label>
                        {def.length > 70 || def.includes('\n') ? <textarea rows={3} {...props} /> : <input type="text" {...props} />}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
          {visibles.length === MAX_VISIBLES && <p className="admin-page-sub">{t('admin.textos.mas', { defaultValue: 'Hay más resultados: afina la búsqueda o elige una sección.' })}</p>}
          <div className="mw-guardar-barra">
            <span className="mw-ayuda">{pendientes ? t('admin.textos.pendientes', { defaultValue: '{{n}} texto(s) sin guardar', n: pendientes }) : ''}</span>
            {ok && <span className="mw-ok"><Check size={15} /> {t('admin.mw.guardado')}</span>}
            <button type="button" className="btn-primary" onClick={guardar} disabled={guardando || !pendientes}>
              {guardando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
